import { z } from 'zod';
/**
 * Who the caller is, once a token has been verified.
 *
 * Roles are a closed set on purpose. An unrecognised role is a rejection rather
 * than a pass — a token carrying `role: "superadmin"` from a project that once
 * had such a thing must not become an unbounded permission here.
 */
export const appRoleSchema = z.enum(['user', 'admin']);
/** The claims this API reads. Supabase puts custom claims under `app_metadata`. */
export const supabaseClaimsSchema = z.object({
    sub: z.string().min(1),
    email: z.email().optional(),
    app_metadata: z
        .object({ roles: z.array(appRoleSchema).optional() })
        .optional(),
});
export const authenticatedUserSchema = z.object({
    /**
     * The pseudonymous handle, not the Supabase uid. Everything downstream — logs,
     * traces, analytics — sees only this, so a leak of any of them is not a leak of
     * the identity provider's primary key.
     */
    id: z.string().min(1),
    roles: z.array(appRoleSchema),
    /**
     * Read only to name an admin: who queued a generation job (GEN-111) and who signed a
     * lesson's review (ADMIN-23). Never log it; `id` is what telemetry gets.
     */
    email: z.email().optional(),
});
/**
 * What `GET /me` answers.
 *
 * `roles` is read from the verified token rather than from the stored row. Two
 * places to learn what a caller may do is two places for a permission to drift, and
 * the identity provider is the one that issued it.
 */
/** What `Date.prototype.toISOString` produces, which is what the API sends. */
const ISO_8601_UTC = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
export const meResponseSchema = z.object({
    /** The pseudonymous handle. Never the Supabase uid. */
    id: z.string().min(1),
    roles: z.array(appRoleSchema),
    /**
     * ISO 8601, UTC. When this API first saw the account.
     *
     * A plain string with a pattern rather than `z.iso.datetime()`, which is squeezed
     * from both sides: `z.iso.datetime()` builds a ZodISODateTime, and nestjs-zod
     * 5.5.0's OpenAPI metadata factory throws `Cannot use 'in' operator to search for
     * '_zod' in undefined` on it — failing `openapi:generate` with a stack trace that
     * names neither the schema nor the field — while the older `.datetime()` spelling
     * is deprecated and the lint rule rejects it.
     */
    createdAt: z
        .string()
        .regex(ISO_8601_UTC, 'must be an ISO 8601 UTC timestamp'),
});
/**
 * The roles a signed-in client should believe it has, read from the token's
 * `app_metadata`.
 *
 * Shared because both apps need it and would otherwise write it slightly
 * differently, which is how one ends up rendering an admin menu that every click
 * 403s on. This hides navigation and nothing more — the API's guard is the boundary.
 *
 * Unrecognised roles are dropped for that reason. An absent claim reads as
 * `['user']`, matching what the guard assumes.
 */
export function rolesFromAppMetadata(appMetadata) {
    const claimed = typeof appMetadata === 'object' && appMetadata !== null
        ? appMetadata.roles
        : undefined;
    if (!Array.isArray(claimed))
        return ['user'];
    const known = claimed.filter((value) => appRoleSchema.safeParse(value).success);
    return known.length > 0 ? known : ['user'];
}
/**
 * The roles an account should carry after granting or revoking one.
 *
 * Lives here rather than in the script that calls it because the closed set is
 * defined here, and because a role calculation nobody can unit-test is a role
 * calculation that gets it wrong against a live identity provider.
 *
 * Anything unrecognised in `current` is dropped. The guard already rejects unknown
 * roles, so leaving one in place would preserve a value that can only ever cause a
 * rejection — and `app_metadata` is writable from the dashboard, so unrecognised
 * values are a real possibility rather than a theoretical one.
 */
export function rolesAfter(current, change) {
    const known = new Set((Array.isArray(current) ? current : []).filter((value) => appRoleSchema.safeParse(value).success));
    if ('grant' in change)
        known.add(change.grant);
    else
        known.delete(change.revoke);
    // Every account is at least a user, which is what the guard assumes when the
    // claim is absent entirely.
    known.add('user');
    return appRoleSchema.options.filter((role) => known.has(role));
}
//# sourceMappingURL=identity.js.map